import type { ComponentProps, ComponentType } from "react";

import { isPreviewing } from "@builder.io/sdk-react";
import { z } from "zod";

// Map Builder.io input types to Zod definitions
const BUILDER_INPUT_TYPES_TO_ZOD_DEFINITION = {
  string: z.string(),
  number: z.number(),
  boolean: z.boolean(),
  longText: z.string(),
  richText: z.string(),
  file: z.string(),
  color: z.string(),
  date: z.string(),
  email: z.string(),
  url: z.string(),
  object: z.object({}),
  list: z.array(z.object({})),
  enum: z.string(),
  reference: z.object({ "@type": z.literal("@builder.io/core:Reference") }),
  uiBlocks: z.array(z.object({})),

  // Custom input types provided by plugins
  richTextEditor: z.string(),
  tableEditor: z.string(),
} as const;

type BuilderInputType = keyof typeof BUILDER_INPUT_TYPES_TO_ZOD_DEFINITION;

export function withValidation<
  T extends Inputs,
  // Disabled to make props type inference work
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component extends ComponentType<any>
>(
  inputs: T,
  Component: Component,
  name: string
): ComponentType<ComponentProps<Component>> {
  return (props: ComponentProps<Component>) => {
    if (isPreviewing()) {
      return <Component {...props} />;
    }
    const schema = buildSchema(inputs);
    const { success, error } = schema.safeParse(props);

    if (!success) {
      console.error({
        componentName: name,
        errors: error.errors,
      });

      return null;
    }

    return <Component {...props} />;
  };
}

const checkOptionalFields = (schema: z.ZodTypeAny, isRequired?: boolean) => {
  return isRequired ? schema : schema.optional().nullable();
};

// Build a Zod schema from the Builder.io component inputs
function buildSchema(inputs: Inputs): z.AnyZodObject {
  return inputs.reduce((schema, entry) => {
    if (!isValidBuilderInputType(entry.type)) {
      throw new Error(`Unsupported input type: ${entry.type}`);
    }

    // Handle nested objects edge case
    if (entry.type === "object" && entry.subFields) {
      const zodObjectDefinition = buildSchema(entry.subFields);
      return schema.merge(
        z.object({
          [entry.name]: checkOptionalFields(
            zodObjectDefinition,
            entry.required
          ),
        })
      );
    }

    // Handle list of objects edge case
    if (entry.type === "list" && entry.subFields) {
      const zodArrayDefinition = z.array(buildSchema(entry.subFields));
      return schema.merge(
        z.object({
          [entry.name]: checkOptionalFields(zodArrayDefinition, entry.required),
        })
      );
    }

    const zodTypeDefinition = BUILDER_INPUT_TYPES_TO_ZOD_DEFINITION[entry.type];

    const entrySchema = z.object({
      [entry.name]: checkOptionalFields(zodTypeDefinition, entry.required),
    });

    return schema.merge(entrySchema);
  }, z.object({}));
}

// Check if the input type is supported
function isValidBuilderInputType(
  inputType: string
): inputType is BuilderInputType {
  return inputType in BUILDER_INPUT_TYPES_TO_ZOD_DEFINITION;
}
